import { Typography } from '@euler/app/components/typography';
import { kCustomConstructionWorkItemCategoryId } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/usePredefinedConstructionItems';
import { useHandleConstructionWorkItemPress } from '@euler/app/flows/task/pages/construction/functions/useHandleConstructionWorkItemPress';
import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { nanoid } from 'nanoid/non-secure';
import { memo, useCallback, useMemo } from 'react';
import { SectionList, TouchableOpacity, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';
import {
  useConstructionJobScheduleContext,
  useScheduleConstructionJobs,
} from './ConstructionJobScheduleContext';
import { WorkItemCell } from './components/WorkItemCell';
import { useWorkItemsBottomSheetFooterHeight } from './components/WorkItemsBottomSheet';
import {
  ConstructionWorkItemCategory,
  ConstructionWorkItemInfo,
} from './functions/useConstructionWorkItems';

type Section = {
  title: string;
  data: ConstructionWorkItemInfo[];
  clearable?: boolean;
  emptyText?: string;
};

export const WorkItemsTabContentScreen = wrapNavigatorScreen(
  ({
    id,
    categories$,
  }: {
    categories$: BehaviorSubject<ConstructionWorkItemCategory[]>;
    id: string;
  }) => {
    const confirm = useConfirm();
    const { rpx } = useResponsiveContext();

    const [categories] = useBehaviorSubject(categories$);
    const category = useMemo(
      () =>
        categories.find(x => x.id === id) ?? {
          id: nanoid(),
          name: 'N/A',
          groups: [],
          template: {
            id: nanoid(),
            procedures: [],
          },
        },
      [categories, id],
    );

    const sections = useMemo<Section[]>(
      () =>
        category.groups.map(group => ({
          title: group.name,
          data: group.items,
          clearable:
            category.id === kCustomConstructionWorkItemCategoryId &&
            category.groups.flatMap(g => g.items).length > 0,
          emptyText:
            category.id === kCustomConstructionWorkItemCategoryId
              ? '暂无相关自定义项目'
              : undefined,
        })),
      [category],
    );

    const { clearCustomWorkItems, removeCustomWorkItem } =
      useConstructionJobScheduleContext();
    const { selectedWorkItems } = useScheduleConstructionJobs();

    const onItemPress = useHandleConstructionWorkItemPress();
    const bottomSheetFooterHeight = useWorkItemsBottomSheetFooterHeight();

    const onClear = usePersistFn(async () => {
      if (
        await confirm({
          title: '确认',
          message: '您即将清空所有自定义施工项目，该操作不可撤销，是否继续? ',
        })
      ) {
        clearCustomWorkItems();
      }
    });

    const onDelete = usePersistFn(async (item: ConstructionWorkItemInfo) => {
      if (
        await confirm({
          title: '确认',
          message: '确认删除该自定义施工项目? ',
        })
      ) {
        removeCustomWorkItem(item.name);
      }
    });

    return (
      <>
        <SectionList
          sections={sections}
          keyExtractor={item => item.name}
          stickySectionHeadersEnabled={true}
          contentContainerStyle={{
            paddingBottom: bottomSheetFooterHeight + rpx(15),
          }}
          showsVerticalScrollIndicator={false}
          renderSectionHeader={({ section }) => (
            <SectionHeader
              title={section.title}
              count={section.data.length}
              clearable={section.clearable}
              onClear={onClear}
            />
          )}
          renderSectionFooter={({ section }) =>
            section.data.length === 0 ? (
              <Typography.Label
                css={`
                  margin: 0 ${rpx(25)}px;
                `}
                light
                color="#777"
                size={rpx(14)}
              >
                {section.emptyText ?? `暂无相关${section.title}项目`}
              </Typography.Label>
            ) : (
              <></>
            )
          }
          renderItem={({ item, section, index }) => (
            <Cell
              item={item}
              deletable={section.clearable}
              selected={selectedWorkItems.some(
                x => x.workItem.name === item.name,
              )}
              onPress={onItemPress}
              onDelete={onDelete}
              first={index === 0}
              last={index === section.data.length - 1}
            />
          )}
          css={`
            flex: 1;
            background-color: #f3f3f3;
          `}
        />
      </>
    );
  },
);

const SectionHeader = memo(
  ({
    title,
    count,
    clearable,
    onClear,
  }: {
    title: string;
    count: number;
    clearable?: boolean;
    onClear?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      construction: {
        schedule: {
          workItem: {
            listSection: { bgColor, titleColor },
          },
        },
      },
    } = useTheme();
    return (
      <View
        css={`
          padding: ${rpx(15)}px ${rpx(25)}px;
          background-color: ${bgColor};
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <Typography.Label
          bold
          size={rpx(16)}
          css={`
            color: ${titleColor};
          `}
        >{`${title} (${count}项)`}</Typography.Label>
        {clearable ? (
          <TouchableOpacity onPress={onClear}>
            <Typography.Label bold color={StatusColors.Danger} size={rpx(14)}>
              清空
            </Typography.Label>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  },
);

const Cell = memo(
  ({
    item,
    selected,
    deletable,
    onPress,
    onDelete,
    first,
    last,
  }: {
    first?: boolean;
    last?: boolean;
    item: ConstructionWorkItemInfo;
    selected: boolean;
    deletable?: boolean;
    onPress: (item: ConstructionWorkItemInfo) => void;
    onDelete: (item: ConstructionWorkItemInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    const onDeletePress = useCallback(() => {
      onDelete?.(item);
    }, [item, onDelete]);

    return (
      <WorkItemCell
        title={item.name}
        selected={selected}
        deletable={deletable}
        onDelete={onDeletePress}
        onPress={onCellPress}
        onRemove={onCellPress}
        onAdd={onCellPress}
        titleStyle={{
          color: textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(15),
        }}
        css={`
          background-color: ${bgLight};
          margin: ${rpx(5)}px ${rpx(10)}px;
          margin-top: ${first ? 0 : rpx(5)}px;
          margin-bottom: ${rpx(last ? 15 : 5)}px;
        `}
      />
    );
  },
);
